const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

class APIClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // Read the response body as text first (can only read once)
      const textBody = await response.text();

      // Try to parse as JSON
      let data;
      try {
        data = JSON.parse(textBody);
      } catch (e) {
        // Not JSON, treat as error text
        data = { error: textBody || 'Invalid response from server' };
      }

      if (!response.ok) {
        throw new Error(data.error || data.message || `API request failed with status ${response.status}`);
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Party A endpoints
  async initDispute(email, name) {
    return this.request('/dispute/init', {
      method: 'POST',
      body: JSON.stringify({ email, name }),
    });
  }

  async classifyDispute(disputeId, type, amountClaimed) {
    return this.request(`/dispute/${disputeId}/classify`, {
      method: 'POST',
      body: JSON.stringify({ type, amountClaimed }),
    });
  }

  async saveAssessment(disputeId, partyId, answers) {
    return this.request(`/dispute/${disputeId}/assessment`, {
      method: 'POST',
      body: JSON.stringify({ disputeId, partyId, party: 'partyA', answers }),
    });
  }

  async saveParameters(disputeId, partyId, parameters) {
    return this.request(`/dispute/${disputeId}/parameters`, {
      method: 'POST',
      body: JSON.stringify({ disputeId, partyId, party: 'partyA', parameters }),
    });
  }

  async uploadDocument(disputeId, partyId, file, category) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('partyId', partyId);
    formData.append('category', category);

    return this.request(`/dispute/${disputeId}/documents`, {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async sendInvitation(disputeId, recipientEmail, recipientName, tone) {
    return this.request(`/dispute/${disputeId}/invite`, {
      method: 'POST',
      body: JSON.stringify({ recipientEmail, recipientName, tone }),
    });
  }

  async getDisputeStatus(disputeId, party) {
    const params = party ? `?party=${party}` : '';
    return this.request(`/dispute/${disputeId}/status${params}`);
  }

  // Party B endpoints
  async getRespondView(token) {
    return this.request(`/respond/${token}`);
  }

  async prepareMode(token, emotionalState) {
    return this.request(`/respond/${token}/prepare`, {
      method: 'POST',
      body: JSON.stringify({ emotionalState }),
    });
  }

  async saveRespondAssessment(token, partyId, answers) {
    return this.request(`/respond/${token}/assessment`, {
      method: 'POST',
      body: JSON.stringify({ partyId, answers }),
    });
  }

  async saveRespondParameters(token, partyId, parameters) {
    return this.request(`/respond/${token}/parameters`, {
      method: 'POST',
      body: JSON.stringify({ partyId, parameters }),
    });
  }

  async checkZone(token) {
    return this.request(`/respond/${token}/zone-check`);
  }

  // Settlement endpoints
  async previewSettlement(settlementId, partyId) {
    return this.request(`/settlement/${settlementId}/preview?partyId=${partyId}`);
  }

  async acceptSettlement(settlementId, partyId) {
    return this.request(`/settlement/${settlementId}/accept`, {
      method: 'POST',
      body: JSON.stringify({ partyId, acceptance: true }),
    });
  }

  async signSettlement(settlementId, partyId, signature) {
    return this.request(`/settlement/${settlementId}/sign`, {
      method: 'POST',
      body: JSON.stringify({ partyId, signature }),
    });
  }

  // Payment endpoints
  async createGoodFaithPayment(settlementId, partyId) {
    return this.request('/payment/good-faith', {
      method: 'POST',
      body: JSON.stringify({ settlementId, partyId }),
    });
  }

  async createInitialPayment(settlementId) {
    return this.request('/payment/initial', {
      method: 'POST',
      body: JSON.stringify({ settlementId }),
    });
  }

  async getPaymentStatus(paymentId) {
    return this.request(`/payment/status/${paymentId}`);
  }

  // Attorney endpoints
  async attorneyLogin(email, password) {
    return this.request('/attorney/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async getAttorneyDashboard(token) {
    return this.request('/attorney/dashboard', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getCaseDetails(caseId, token) {
    return this.request(`/attorney/case/${caseId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async approveCase(caseId, token, modifications = {}) {
    return this.request(`/attorney/case/${caseId}/approve`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(modifications),
    });
  }

  async getAnalytics(token) {
    return this.request('/attorney/analytics', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async getAttorneyAnalytics(period, token) {
    return this.request(`/attorney/analytics?period=${period}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  async requestModifications(caseId, token, feedback, questionsToImprove) {
    return this.request(`/attorney/case/${caseId}/request-modifications`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ feedback, questionsToImprove }),
    });
  }

  async getFeedback(disputeId, partyId) {
    return this.request(`/dispute/${disputeId}/feedback?partyId=${partyId}`);
  }
}

export const api = new APIClient(API_BASE_URL);
export default api;

// Named exports for convenience
export const initDispute = (data) => api.request('/dispute/init', {
  method: 'POST',
  body: JSON.stringify(data),
});

export const classifyDispute = (data) => api.request(`/dispute/${data.disputeId}/classify`, {
  method: 'POST',
  body: JSON.stringify({ type: data.type, amountClaimed: data.amountClaimed }),
});

export const saveAssessment = (data) => api.request(`/dispute/${data.disputeId}/assessment`, {
  method: 'POST',
  body: JSON.stringify({
    disputeId: data.disputeId,
    partyId: data.partyId,
    party: 'partyA',
    answers: data.answers
  }),
});

export const saveParameters = (data) => api.request(`/dispute/${data.disputeId}/parameters`, {
  method: 'POST',
  body: JSON.stringify({
    disputeId: data.disputeId,
    partyId: data.partyId,
    party: 'partyA',
    parameters: data.parameters
  }),
});

export const sendInvitation = (data) => api.request(`/dispute/${data.disputeId}/invite`, {
  method: 'POST',
  body: JSON.stringify({
    recipientEmail: data.recipientEmail,
    recipientName: data.recipientName,
    tone: data.tone,
    customMessage: data.customMessage,
  }),
});

export const getDisputeStatus = (disputeId) => api.request(`/dispute/${disputeId}/status`);
